/**
 * Email to Inbox Link Generator
 * Generates direct links to email provider inboxes based on email addresses
 */

interface ProviderConfig {
  name: string;
  url: string;
}

const EMAIL_PROVIDERS: Record<string, ProviderConfig> = {
  // Major providers
  "gmail.com": {
    name: "Gmail",
    url: "https://mail.google.com/",
  },
  "googlemail.com": {
    name: "Gmail",
    url: "https://mail.google.com/",
  },
  "yahoo.com": {
    name: "Yahoo Mail",
    url: "https://mail.yahoo.com/",
  },
  "icloud.com": {
    name: "iCloud Mail",
    url: "https://www.icloud.com/mail",
  },
  "me.com": {
    name: "iCloud Mail",
    url: "https://www.icloud.com/mail",
  },
  "pm.me": {
    name: "ProtonMail",
    url: "https://mail.proton.me/",
  },
  "protonmail.com": {
    name: "ProtonMail",
    url: "https://mail.proton.me/",
  },
  "gmx.ch": {
    name: "GMX",
    url: "https://www.gmx.ch/",
  },
  "gmx.de": {
    name: "GMX",
    url: "https://www.gmx.de/",
  },
  "web.de": {
    name: "WEB.DE",
    url: "https://web.de/",
  },
  // Swiss providers
  "bluewin.ch": {
    name: "Bluewin",
    url: "https://webmail.bluewin.ch/",
  },
  "sunrise.ch": {
    name: "Sunrise",
    url: "https://mip.sunrise.ch/",
  },
  "hispeed.ch": {
    name: "Hispeed",
    url: "https://upcmail.hispeed.ch/",
  },
};

/**
 * Extract domain from email address
 */
function extractDomain(email: string): string | null {
  const match = email
    .toLowerCase()
    .trim()
    .match(/@(.+)$/);
  return match ? match[1] : null;
}

/**
 * Check if domain matches Outlook/Hotmail pattern
 */
function isOutlookDomain(domain: string): boolean {
  return /^(hotmail|outlook|live)\.[a-z]{2,}$/.test(domain);
}

/**
 * Get inbox URL for an email address
 */
export function getInboxLink(email: string): string | null {
  const domain = extractDomain(email);
  if (!domain) return null;

  // Check for Outlook/Hotmail/Live with any TLD
  if (isOutlookDomain(domain)) {
    return "https://outlook.live.com/mail/0/inbox";
  }

  const provider = EMAIL_PROVIDERS[domain];
  if (!provider) return null;

  return provider.url;
}

/**
 * Get provider name for an email address
 */
export function getProviderName(email: string): string | null {
  const domain = extractDomain(email);
  if (!domain) return null;

  // Check for Outlook/Hotmail/Live with any TLD
  if (isOutlookDomain(domain)) {
    return "Outlook";
  }

  const provider = EMAIL_PROVIDERS[domain];
  return provider ? provider.name : null;
}

/**
 * Check if email provider is supported
 */
export function isProviderSupported(email: string): boolean {
  const domain = extractDomain(email);
  if (!domain) return false;

  // Check for Outlook/Hotmail/Live with any TLD
  if (isOutlookDomain(domain)) {
    return true;
  }

  return domain in EMAIL_PROVIDERS;
}

/**
 * Create an HTML link to inbox
 */
export function createInboxLink(
  email: string,
  linkText?: string,
): string | null {
  const url = getInboxLink(email);
  if (!url) return null;

  const providerName = getProviderName(email);
  const text = linkText
    ? linkText.replace("%s", providerName || "inbox")
    : `Check your ${providerName || "email"} inbox`;

  return `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`;
}

/**
 * Get all supported domains
 */
export function getSupportedDomains(): string[] {
  return Object.keys(EMAIL_PROVIDERS);
}
